import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

function delcampeReferenceQuery(value) {
  const match = value.trim().match(/^(?:(?:DC|Delcampe)[\s:#-]*)?(\d{8,12})$/i);
  return match ? 'tag:delcampe-id-' + match[1] : value;
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    // Restore the literal query without decoding HTML entities a second time.
    const input = this.refs.searchPageInput;
    const query = new URL(window.location.href).searchParams.get('q');
    if (query !== null && input.value === input.defaultValue) {
      const displayQuery = query.replace(/^tag:delcampe-id-(\d{8,12})$/i, '$1');
      input.value = displayQuery;
      input.defaultValue = displayQuery;
    }
    this.refs.searchPageInput.form?.addEventListener('submit', this.handleReferenceSubmit);
  }

  disconnectedCallback() {
    this.refs.searchPageInput.form?.removeEventListener('submit', this.handleReferenceSubmit);
    super.disconnectedCallback();
  }

  handleReferenceSubmit = () => {
    const input = this.refs.searchPageInput;
    input.value = delcampeReferenceQuery(input.value);
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
